import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import { Button, TextField, LinearProgress } from "@material-ui/core";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import GetAppIcon from "@material-ui/icons/GetApp";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import "./VenuePaginationToolbar.css";

@inject("agentVenueStore", "profileStore")
@observer
class VenuePaginationToolbar extends Component {
    state = {
        pageInputValue: "1"
    };

    componentDidMount() {
        this.setState({ pageInputValue: String(this.props.agentVenueStore.agentVenuePage) });
    }

    componentDidUpdate(prevProps) {
        const currentPage = this.props.agentVenueStore.agentVenuePage;
        const prevPage = prevProps.agentVenueStore.agentVenuePage;
        if (prevPage !== currentPage) {
            this.setState({ pageInputValue: String(currentPage) });
        }
    }

    handlePreviousPage = () => {
        const { agentVenueStore } = this.props;
        const currentPage = agentVenueStore.agentVenuePage;
        if (currentPage > 1) {
            this.props.onLoadPage(currentPage - 1);
        }
    };

    handleNextPage = () => {
        const { agentVenueStore } = this.props;
        if (agentVenueStore.isMoreAgentVenues) {
            const currentPage = agentVenueStore.agentVenuePage;
            this.props.onLoadPage(currentPage + 1);
        }
    };

    handlePageInputChange = (e) => {
        this.setState({ pageInputValue: e.target.value });
    };

    handlePageInputKeyDown = (e) => {
        if (e.key === "Enter") {
            this.handlePageInputBlur();
        }
    };

    handlePageInputBlur = () => {
        const { pageInputValue } = this.state;
        const { agentVenueStore } = this.props;
        const pageNum = parseInt(pageInputValue, 10);
        const highestLoaded = agentVenueStore.highestLoadedPage;
        
        if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= highestLoaded) {
            this.props.onLoadPage(pageNum);
        } else {
            this.setState({ pageInputValue: String(agentVenueStore.agentVenuePage) });
        }
    };

    handleLoadAll = () => {
        const { agentVenueStore, profileStore } = this.props;
        const currentProfile = profileStore.getCurrentProfile();
        const isConsultant = currentProfile?.isConsultant;
        const profileId = isConsultant ? currentProfile?.id : currentProfile?.id;
        agentVenueStore.loadAllVenues(profileId, isConsultant);
    };

    handleCancelLoadAll = () => {
        const { agentVenueStore, onReset } = this.props;
        agentVenueStore.cancelLoadAllVenues();
        setTimeout(() => {
            if (onReset && agentVenueStore.agentVenues.length === 0) {
                onReset();
            }
        }, 100);
    };

    handleResetToPaginated = () => {
        const { onReset } = this.props;
        if (onReset) {
            onReset();
        }
    };

    isAllLoaded = () => {
        const { agentVenueStore } = this.props;
        return !agentVenueStore.isMoreAgentVenues && agentVenueStore.agentVenues.length > 0;
    };

    render() {
        const { agentVenueStore } = this.props;
        const { venuePaginationState, agentVenues, isMoreAgentVenues, agentVenuePage, isLoadingVenues } = agentVenueStore;
        const { isLoadingAll, loadAllProgress, totalLoaded } = venuePaginationState;
        const { pageInputValue } = this.state;

        const allLoaded = this.isAllLoaded() && !isLoadingAll;
        const hasPrevious = agentVenuePage > 1;
        const hasNext = isMoreAgentVenues;

        if (isLoadingAll) {
            return (
                <div className="venue-pagination-toolbar">
                    <div className="venue-pagination-loading-all">
                        <div className="venue-loading-progress-container">
                            <LinearProgress 
                                variant="determinate" 
                                value={loadAllProgress} 
                                className="venue-loading-progress-bar"
                            />
                        </div>
                        <span className="venue-loading-progress-text">
                            Loading... <strong>{totalLoaded}</strong> venues
                        </span>
                        <Button 
                            size="small" 
                            onClick={this.handleCancelLoadAll}
                            className="venue-cancel-load-btn"
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            );
        }

        if (allLoaded) {
            return (
                <div className="venue-pagination-toolbar venue-pagination-toolbar-all-loaded">
                    <div className="venue-all-loaded-info">
                        <CheckCircleIcon className="venue-all-loaded-icon" />
                        <span>Showing all <strong>{agentVenues.length}</strong> venues</span>
                    </div>
                    <button 
                        className="venue-reset-view-link"
                        onClick={this.handleResetToPaginated}
                    >
                        Reset to paginated view
                    </button>
                </div>
            );
        }

        if (!isMoreAgentVenues && agentVenues.length === 0) {
            return null;
        }

        const displayTotal = isMoreAgentVenues ? `${agentVenues.length}+` : agentVenues.length;

        return (
            <div className="venue-pagination-toolbar">
                <div className="venue-pagination-info">
                    Showing <strong>{agentVenues.length}</strong> of <strong>{displayTotal}</strong> venues
                </div>

                <div className="venue-pagination-controls">
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={this.handlePreviousPage}
                        disabled={!hasPrevious || isLoadingVenues}
                        className="venue-pagination-nav-btn"
                    >
                        <NavigateBeforeIcon fontSize="small" />
                        Prev
                    </Button>

                    <div className="venue-page-selector">
                        <span>Page</span>
                        <TextField
                            value={pageInputValue}
                            onChange={this.handlePageInputChange}
                            onBlur={this.handlePageInputBlur}
                            onKeyDown={this.handlePageInputKeyDown}
                            variant="outlined"
                            size="small"
                            className="venue-page-input"
                            inputProps={{ style: { textAlign: "center", padding: "6px 8px" } }}
                            disabled={isLoadingVenues}
                            title="Enter a page number (1 to current page)"
                        />
                        <span className="venue-total-pages">of {agentVenueStore.highestLoadedPage}{isMoreAgentVenues ? "+" : ""}</span>
                    </div>

                    <Button
                        variant="contained"
                        size="small"
                        onClick={this.handleNextPage}
                        disabled={!hasNext || isLoadingVenues}
                        className="venue-pagination-nav-btn venue-pagination-nav-btn-primary"
                    >
                        Next
                        <NavigateNextIcon fontSize="small" />
                    </Button>
                </div>

                {isMoreAgentVenues && (
                    <div className="venue-load-all-section">
                        <div className="venue-divider" />
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={this.handleLoadAll}
                            disabled={isLoadingVenues}
                            className="venue-load-all-btn"
                        >
                            <GetAppIcon fontSize="small" style={{ marginRight: 4 }} />
                            Load All
                        </Button>
                    </div>
                )}
            </div>
        );
    }
}

export default VenuePaginationToolbar;
